import React from "react";
import "./about.scss";
import Footer from "../../components/footer/footer";
import Navbar from "../../components/navbar/navbar";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

function About() {
    const items = [
        "Personal hygiene and bathing",
        "Dressing and continence care",
        "Toileting and mobility support",
        "Medication prompts and administration",
        "Support with eating and nutrition",
        "Rehabilitation and reablement",
        "Clinical and nurse-led interventions",
        "Emotional wellbeing and mental health support",
        "Transition services for young people",
        "Family support and behavioural assistance",
        "Complex Care",
        "Children & Young People's Services",
    ];
  return (
    <>
      <Navbar />

      <section className="about-container">
        <h1 className="title">About Daily Help United</h1>
        <h2 className="subtitle">Care, Support and Nursing at Home</h2>

        <p className="description">
          Daily Help United Limited is a specialist home care provider offering comprehensive <Link to="/">care services</Link> to clients in their own homes or community.
        </p>

        <div className="content-section">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-block">
            <p>
                Daily Help United delivers healthcare staffing solutions, providing skilled Health Care 
                Assistants and Registered Nurses to hospitals, care homes, medical facilities, and community 
                healthcare settings across the region. Our people-first approach prioritizes patient wellbeing, 
                partner satisfaction, and staff development, building lasting relationships based on trust and 
                clinical excellence.
            </p>

            <p>
              Our team brings decades of combined experience across hospital care, nursing homes, domiciliary care, 
              and specialized settings. We understand healthcare pressures and respond with speed, flexibility, and 
              professionalism to match your specific needs with carefully vetted professionals.
            </p>

            <p>
              We don't just fill vacancies—we deliver quality placements through rigorous recruitment, ongoing training, 
              and continuous monitoring. Every professional we place meets the highest clinical and professional standards, 
              ensuring you receive the exceptional care your patients deserve.
            </p>

          </motion.div>

          <div className="image-block">
            <img
              src="https://images.pexels.com/photos/7345465/pexels-photo-7345465.jpeg"
              alt="Daily Help United Team"
            />
          </div>
        </div>
      </section>

      <section className="why-choose">
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="why-header">
            <img
            src="https://res.cloudinary.com/dsnontai6/image/upload/v1762109180/daily-help-united-high-resolution-logo-grayscale-transparent-2_ttattq.png"
            alt="Logo"
            className="why-logo"
            />
            <h2>Why Choose Daily Help United?</h2>
            <p className="tagline">Enriching lives & supporting independence</p>
        </motion.div>

        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="why-grid">
            {/* Card 1 */}
            <div className="why-item">
            <img src="https://res.cloudinary.com/dsnontai6/image/upload/v1763413914/networking_jun1aa.png" alt="Network" className="icon" />
            <div>
                <h3>Fast mobilisation network:</h3>
                <p>
                Because of our extensive branch network, in-house training team and
                large care staff, we have the resources to mobilise packages of
                care quickly.
                </p>
            </div>
            </div>

            {/* Card 2 */}
            <div className="why-item">
            <img src="https://res.cloudinary.com/dsnontai6/image/upload/v1763413914/people_xwahxo.png" alt="Team" className="icon" />
            <div>
                <h3>Expert team:</h3>
                <p>
                Our dedicated care assistants and registered nurses lead our
                complex care division – compassionate, highly skilled and caring.
                </p>
            </div>
            </div>

            {/* Card 3 */}
            <div className="why-item">
            <img src="https://res.cloudinary.com/dsnontai6/image/upload/v1763413914/innovation_jaebvv.png" alt="Innovation" className="icon" />
            <div>
                <h3>Constantly innovating:</h3>
                <p>
                We use the latest digital care software, including digital care
                plans and medication records, constantly improving the care we
                provide.
                </p>
            </div>
            </div>

            {/* Card 4 */}
            <div className="why-item">
            <img src="https://res.cloudinary.com/dsnontai6/image/upload/v1763413913/achievement-award_g7ygfs.png" alt="Award" className="icon" />
            <div>
                <h3>Highest rated home care provider:</h3>
                <p>
                Rated top 20 home care provider for six consecutive years. Recognised
                and awarded for impactful, high-quality care training.
                </p>
            </div>
            </div>

            {/* Card 5 */}
            <div className="why-item">
            <img src="https://res.cloudinary.com/dsnontai6/image/upload/v1763413914/genealogy_kbxpb2.png" alt="Family" className="icon" />
            <div>
                <h3>Family owned, with real family values:</h3>
                <p>
                We are family-run, with family values at the heart of everything we
                do for both our clients and staff.
                </p>
            </div>
            </div>
        </motion.div>
        </section>

        <motion.section 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="offer-section">
            <h2>What we offer</h2>

            <p className="intro">
                We are proud to offer a wide range of high-quality, person-centred care
                services designed to support people of all ages, needs, and circumstances.
                From rehabilitation following a hospital stay to long-term complex care,
                mental health support and family-based services for children and young
                people — our care is always tailored, flexible and grounded in real
                understanding.
            </p>

            <p className="intro">
                What sets us apart is not just what we do, but how we do it. As a
                family-run provider, we genuinely care for every individual we support.
                Our experienced, compassionate team works closely with people and their
                families to deliver care that promotes independence, dignity and quality
                of life; whether that’s through short-term assistance or ongoing,
                specialist support.
            </p>

            <p className="assist-title">We can assist with:</p>

            <div className="offer-grid">
                {items.map((item, index) => (
                <div className="offer-card" key={index}>
                    <div className="check-icon">
                    ✓
                    </div>
                    <p>{item}</p>
                </div>
                ))}
            </div>

            <p className="mission">
                Our mission is to be a trusted solution to the challenges individuals and
                modern families face today, and we do that by offering care that is
                personal, adaptable and focused on meaningful outcomes.
            </p>

            <p className="more-info">
                If you'd like to learn more about how we can support you or a loved one,
                please get in touch with our friendly team today.
            </p>

            <div className="center-btn">
                <Link to="/contact" className="contact-btn">Contact Us</Link>
            </div>
        </motion.section>
      <Footer />
    </>
  );
}

export default About;
